import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import type { SupportTicketDTO, CreateTicketPayload } from '@practium/features/support';
import { addMessageSchema, createTicketSchema } from '@practium/features/support';
import { formatDate } from '@practium/shared';
import { Button, FormField, FormInput, FormSectionTitle, Logo } from '@practium/ui';
import { useEffect, useState } from 'react';
import styles from './TicketModal.module.css';

type TicketModalProps =
	| {
			mode: 'create';
			isOpen: boolean;
			onClose: () => void;
			onSubmit: (payload: CreateTicketPayload) => Promise<void>;
			isLoading?: boolean;
			ticket?: never;
	  }
	| {
			mode: 'reply';
			isOpen: boolean;
			onClose: () => void;
			onSubmit: (message: string, ticketId: string) => Promise<void>;
			isLoading?: boolean;
			ticket: SupportTicketDTO | null;
	  };

export const TicketModal = (props: TicketModalProps) => {
	const { mode, isOpen, onClose, isLoading = false } = props;
	const [subject, setSubject] = useState('');
	const [message, setMessage] = useState('');
	const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});

	useEffect(() => {
		if (isOpen) {
			setSubject('');
			setMessage('');
			setErrors({});
		}
	}, [isOpen]);

	if (!isOpen) return null;
	if (mode === 'reply' && !props.ticket) return null;

	const ticket = mode === 'reply' ? props.ticket! : null;
	const createdAt = ticket?.createdAt ? new Date(ticket.createdAt) : new Date();
	const title =
		mode === 'create'
			? `Nova Solicitação - ${formatDate(new Date())}`
			: `Nova Mensagem - ${formatDate(createdAt)}`;
	const submitLabel = mode === 'create' ? 'Enviar Solicitação' : 'Enviar Mensagem';

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) onClose();
	};

	const handleSubmit = async () => {
		if (mode === 'create') {
			const result = createTicketSchema.safeParse({ subject, message });
			if (!result.success) {
				const fieldErrors = result.error.flatten().fieldErrors;
				setErrors({ subject: fieldErrors.subject?.[0], message: fieldErrors.message?.[0] });
				return;
			}
			setErrors({});
			await props.onSubmit({ subject, message });
			setSubject('');
			setMessage('');
		} else {
			const result = addMessageSchema.safeParse({ message });
			if (!result.success) {
				setErrors({ message: result.error.flatten().fieldErrors.message?.[0] });
				return;
			}
			setErrors({});
			await props.onSubmit(message, ticket!._id);
			setMessage('');
		}
	};

	return (
		<div className={styles.overlay} onClick={handleOverlayClick} role="presentation">
			<div className={styles.modal} role="dialog" aria-modal="true">
				<div className={styles.modalTop}>
					<Logo className={styles.logo} />
					<h2 className={styles.modalTitle}>{title}</h2>
					<button
						className={styles.closeButton}
						onClick={onClose}
						disabled={isLoading}
						type="button"
						aria-label="Fechar modal"
					>
						<CloseOutlined className={styles.closeIcon} />
					</button>
				</div>

				<div className={styles.modalContent}>
					<FormSectionTitle>Geral</FormSectionTitle>
					<div className={styles.fields}>
						<FormField label="Tópico" error={errors.subject}>
							<FormInput
								className={`${styles.input} ${errors.subject ? styles.inputError : ''}`}
								value={mode === 'reply' ? ticket!.subject : subject}
								onChange={(e) => mode === 'create' && setSubject(e.target.value)}
								placeholder="Insira o tópico da solicitação"
								hasError={!!errors.subject}
								disabled={isLoading || mode === 'reply'}
							/>
						</FormField>
						<FormField label="Conteúdo" error={errors.message}>
							<textarea
								className={`${styles.textarea} ${errors.message ? styles.textareaError : ''} ${message ? styles.textareaFilled : ''}`}
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder={
									mode === 'create'
										? 'Insira o conteúdo da solicitação'
										: 'Insira o conteúdo da mensagem'
								}
								disabled={isLoading}
							/>
						</FormField>
					</div>
				</div>

				<div className={styles.modalActions}>
					<Button
						variant="primary"
						onClick={handleSubmit}
						isLoading={isLoading}
						icon={<CheckOutlined style={{ fontSize: '1rem' }} />}
						iconPosition="left"
						className={styles.submitBtn}
					>
						{submitLabel}
					</Button>
				</div>
			</div>
		</div>
	);
};
